import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { RichText } from '../components/RichText.jsx'

// Admin → Announcements → New. A standalone page, per the forms-are-pages
// rule (the reader modal behind the bell is the one agreed exception).
//
// Channel is a SINGLE selection (Timotei: "type: site, email, sms,
// WhatsApp(single selection, disable WHATSAPP for now)"). WhatsApp is rendered
// but disabled and carries a note saying why — hiding it entirely would make
// the missing channel look like a bug rather than a decision.
//
// PREVIEW BEFORE SEND, always. An announcement cannot be edited or unsent, so
// this is the only place a wrong filter can be caught. The button below the
// preview is the only thing that sends, and it re-enables itself only after a
// preview of the CURRENT form — change a field and the preview goes stale, so
// you have to look at the new numbers before you can send them.
//
// Everything the server validates is validated by the server: the char
// counter and the disabled states here are ergonomics, and the 422 codes are
// translated below. Nothing is trusted client-side.

const CHANNELS  = ['site', 'email', 'sms', 'whatsapp']
const AUDIENCES = ['all', 'users', 'teachers']

const EMPTY = {
  channel: 'site',
  audience: 'all',
  filter_course_ids: [],
  filter_unpaid: false,
  filter_no_enrollments: false,
  subject: '',
  body: '',
  subject_en: '',
  body_en: '',
}

export function AdminAnnouncementFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user: me, loading: authLoading } = useAuth()

  const [form, setForm] = useState(EMPTY)
  const [courses, setCourses] = useState([])
  const [preview, setPreview] = useState(null)
  const [previewOf, setPreviewOf] = useState(null) // the form the preview describes
  const [settings, setSettings] = useState({ sms_max_chars: 250, whatsapp_enabled: false })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [errorDetail, setErrorDetail] = useState(null)
  const [showEn, setShowEn] = useState(false)

  useEffect(() => {
    if (authLoading || !me || me.role !== 'admin') return
    api.admin.courses.list()
      .then((d) => setCourses(d.courses || d.items || []))
      .catch(() => setCourses([]))
    api.admin.announcements.list(1)
      .then((d) => { if (d.settings) setSettings(d.settings) })
      .catch(() => { /* defaults are fine */ })
  }, [authLoading, me])

  const plain = form.channel === 'sms' || form.channel === 'whatsapp'
  const limited = form.channel === 'sms'
  const bodyLen = form.body.length
  const bodyEnLen = form.body_en.length

  // The preview is only valid for the exact form it was taken of. Serializing
  // is cheaper and more honest than trying to track which field changed.
  const fingerprint = useMemo(() => JSON.stringify(form), [form])
  const stale = previewOf !== null && previewOf !== fingerprint

  function set(patch) { setForm((f) => ({ ...f, ...patch })) }

  function pickChannel(ch) {
    if (ch === 'whatsapp' && !settings.whatsapp_enabled) return
    // Switching between rich and plain keeps the text but drops the subject
    // for SMS, which has none — clearing it here avoids a "subject_required"
    // that the admin cannot see the cause of.
    setPreview(null); setPreviewOf(null); setError(null)
    set({ channel: ch })
  }

  function toggleCourse(id) {
    setForm((f) => ({
      ...f,
      filter_course_ids: f.filter_course_ids.includes(id)
        ? f.filter_course_ids.filter((x) => x !== id)
        : [...f.filter_course_ids, id],
    }))
  }

  function payload() {
    return {
      channel: form.channel,
      audience: form.audience,
      filter_course_ids: form.filter_course_ids,
      filter_unpaid: form.filter_unpaid,
      filter_no_enrollments: form.filter_no_enrollments,
      subject: plain ? '' : form.subject,
      subject_en: plain ? '' : form.subject_en,
      body: form.body,
      body_en: form.body_en,
    }
  }

  // sms_too_long / sms_invalid_chars carry their numbers in the 422 body —
  // "too long" without the limit and the actual length is not an error
  // message, it is a riddle.
  function showError(e) {
    setError(e.code || 'server')
    setErrorDetail(e.data || {})
  }

  async function runPreview() {
    setBusy(true); setError(null); setErrorDetail(null)
    try {
      const d = await api.admin.announcements.preview(payload())
      setPreview(d)
      setPreviewOf(fingerprint)
      if (d.settings) setSettings(d.settings)
    } catch (e) {
      setPreview(null); setPreviewOf(null)
      showError(e)
    } finally { setBusy(false) }
  }

  async function send() {
    const reach = preview?.reach ?? 0
    if (!confirm(t('admin_announcements.confirm_send', {
      count: reach, channel: t(`admin_announcements.channel_${form.channel}`),
    }))) return

    setBusy(true); setError(null); setErrorDetail(null)
    try {
      await api.admin.announcements.send(payload())
      navigate('/admin/announcements')
    } catch (e) {
      showError(e)
      setBusy(false)
    }
  }

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!me || me.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  const canPreview = !busy && (plain ? form.body.trim() !== '' : (form.subject.trim() !== '' && form.body !== ''))
  const canSend = !busy && preview !== null && !stale && (preview.reach ?? 0) > 0

  return (
    <main className="container">
      <p><Link to="/admin/announcements">&larr; {t('admin_announcements.title')}</Link></p>
      <h1>{t('admin_announcements.new_title')}</h1>
      <p className="muted">{t('admin_announcements.new_intro')}</p>

      {error && (
        <p className="error">
          {t(`admin_announcements.errors.${error}`, {
            ...(errorDetail || {}),
            chars: (errorDetail?.chars || []).join(' '),
            defaultValue: t(`admin.errors.${error}`, { defaultValue: error }),
          })}
        </p>
      )}

      {/* One <form> around the whole page so the project's `form label > span`
          styling applies. Enter runs the PREVIEW, never the send — the send
          button is the only thing that sends, on purpose. */}
      <form onSubmit={(e) => { e.preventDefault(); if (canPreview) runPreview() }} noValidate>

        {/* ---- channel ---- */}
        <section className="card">
          <h2>{t('admin_announcements.channel_title')}</h2>
          <div className="checkbox-grid">
            {CHANNELS.map((ch) => {
              const off = ch === 'whatsapp' && !settings.whatsapp_enabled
              return (
                <label key={ch} className="checkbox-label">
                  <input
                    type="radio" name="channel" value={ch}
                    checked={form.channel === ch} disabled={off}
                    onChange={() => pickChannel(ch)}
                  />
                  <span className={off ? 'muted' : undefined}>
                    {t(`admin_announcements.channel_${ch}`)}
                  </span>
                </label>
              )
            })}
          </div>
          <p className="muted small">{t(`admin_announcements.channel_hint_${form.channel}`)}</p>
          {!settings.whatsapp_enabled && (
            <p className="muted small">{t('admin_announcements.whatsapp_off')}</p>
          )}
        </section>

        {/* ---- audience ---- */}
        <section className="card">
          <h2>{t('admin_announcements.audience_title')}</h2>
          <div className="checkbox-grid">
            {AUDIENCES.map((a) => (
              <label key={a} className="checkbox-label">
                <input
                  type="radio" name="audience" value={a}
                  checked={form.audience === a}
                  onChange={() => set({ audience: a })}
                />
                <span>{t(`admin_announcements.audience_${a}`)}</span>
              </label>
            ))}
          </div>
          <p className="muted small">{t('admin_announcements.audience_hint')}</p>

          <h3>{t('admin_announcements.filters_title')}</h3>
          <p className="muted small">{t('admin_announcements.filters_hint')}</p>

          <h4>{t('admin_announcements.filter_courses')}</h4>
          {courses.length === 0 && <p className="muted small">{t('admin_announcements.no_courses')}</p>}
          <div className="checkbox-grid">
            {courses.map((c) => (
              <label key={c.id} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.filter_course_ids.includes(c.id)}
                  onChange={() => toggleCourse(c.id)}
                />
                <span>
                  {c.name}
                  {Number(c.active) === 0 && (
                    <span className="badge muted-badge">{t('admin_announcements.course_inactive')}</span>
                  )}
                </span>
              </label>
            ))}
          </div>
          {form.filter_course_ids.length > 0 && (
            <p className="muted small">{t('admin_announcements.filter_courses_hint')}</p>
          )}

          <label className="checkbox-label">
            <input
              type="checkbox" checked={form.filter_unpaid}
              onChange={(e) => set({ filter_unpaid: e.target.checked })}
            />
            <span>{t('admin_announcements.filter_unpaid')}</span>
          </label>
          <p className="muted small">{t('admin_announcements.filter_unpaid_hint')}</p>

          <label className="checkbox-label">
            <input
              type="checkbox" checked={form.filter_no_enrollments}
              onChange={(e) => set({ filter_no_enrollments: e.target.checked })}
            />
            <span>{t('admin_announcements.filter_no_enrollments')}</span>
          </label>
          <p className="muted small">{t('admin_announcements.filter_no_enrollments_hint')}</p>
        </section>

        {/* ---- content ---- */}
        <section className="card">
          <h2>{t('admin_announcements.content_title')}</h2>

          {!plain && (
            <label>
              <span>{t('admin_announcements.subject')}</span>
              <input
                type="text" maxLength={200}
                value={form.subject} onChange={(e) => set({ subject: e.target.value })}
              />
            </label>
          )}

          <label>
            <span>{t('admin_announcements.body')}</span>
            {plain ? (
              <>
                <textarea
                  rows={6} value={form.body}
                  onChange={(e) => set({ body: e.target.value })}
                />
                <small className={limited && bodyLen > settings.sms_max_chars ? 'error' : 'muted'}>
                  {limited
                    ? t('admin_announcements.sms_counter', { n: bodyLen, limit: settings.sms_max_chars })
                    : t('admin_announcements.wa_counter', { n: bodyLen })}
                </small>
                {limited && <small className="muted">{t('admin_announcements.sms_charset_hint')}</small>}
              </>
            ) : (
              <RichText
                value={form.body} disabled={busy}
                placeholder={t('admin_announcements.body_placeholder')}
                onChange={(html) => set({ body: html })}
              />
            )}
          </label>

          <p>
            <button type="button" className="link-button" onClick={() => setShowEn((v) => !v)}>
              {showEn ? t('admin_announcements.hide_en') : t('admin_announcements.add_en')}
            </button>
          </p>
          <p className="muted small">{t('admin_announcements.en_hint')}</p>

          {showEn && (
            <>
              {!plain && (
                <label>
                  <span>{t('admin_announcements.subject_en')}</span>
                  <input
                    type="text" maxLength={200}
                    value={form.subject_en} onChange={(e) => set({ subject_en: e.target.value })}
                  />
                </label>
              )}
              <label>
                <span>{t('admin_announcements.body_en')}</span>
                {plain ? (
                  <>
                    <textarea
                      rows={6} value={form.body_en}
                      onChange={(e) => set({ body_en: e.target.value })}
                    />
                    <small className={limited && bodyEnLen > settings.sms_max_chars ? 'error' : 'muted'}>
                      {limited
                        ? t('admin_announcements.sms_counter', { n: bodyEnLen, limit: settings.sms_max_chars })
                        : t('admin_announcements.wa_counter', { n: bodyEnLen })}
                    </small>
                  </>
                ) : (
                  <RichText
                    value={form.body_en} disabled={busy}
                    placeholder={t('admin_announcements.body_placeholder')}
                    onChange={(html) => set({ body_en: html })}
                  />
                )}
              </label>
            </>
          )}
        </section>

        {/* ---- preview + send ---- */}
        <section className="card">
          <h2>{t('admin_announcements.preview_title')}</h2>
          <p className="muted small">{t('admin_announcements.preview_hint')}</p>

          <div className="form-actions">
            <button type="submit" disabled={!canPreview}>
              {busy ? t('common.loading') : t('admin_announcements.preview')}
            </button>
          </div>

          {stale && <p className="notice">{t('admin_announcements.preview_stale')}</p>}

          {preview && !stale && (
            <>
              <p>
                <strong>{t('admin_announcements.reach', { count: preview.reach ?? 0 })}</strong>
                {' '}
                <span className="muted">
                  {t('admin_announcements.audience_total', { count: preview.audience?.total ?? 0 })}
                </span>
              </p>

              {(preview.unreachable ?? 0) > 0 && (
                <p className="notice">
                  {t('admin_announcements.unreachable', {
                    count: preview.unreachable,
                    what: form.channel === 'email'
                      ? t('admin_announcements.no_email_word')
                      : t('admin_announcements.no_phone_word'),
                  })}
                </p>
              )}

              {(preview.sample || []).length > 0 && (
                <>
                  <h3>{t('admin_announcements.sample_title')}</h3>
                  <ul>
                    {preview.sample.map((u) => (
                      <li key={u.id}>
                        {u.name}
                        <span className="muted small">
                          {' '}{u.email}{u.phone ? ` · ${u.phone}` : ''}
                          {' · '}{t(`admin_users.role_${u.role}`, { defaultValue: u.role })}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="muted small">{t('admin_announcements.sample_hint')}</p>
                </>
              )}

              <h3>{t('admin_announcements.render_title')}</h3>
              <div className="announcement-preview">
                {preview.render?.subject ? <h4>{preview.render.subject}</h4> : null}
                {plain
                  ? <pre className="sms-preview">{preview.render?.text}</pre>
                  : <div className="announcement-body"
                         dangerouslySetInnerHTML={{ __html: preview.render?.body || '' }} />}
              </div>
              {!preview.has_en && (
                <p className="muted small">{t('admin_announcements.no_en_warning')}</p>
              )}

              <div className="form-actions">
                <button type="button" disabled={!canSend} onClick={send}>
                  {busy ? t('common.loading') : t('admin_announcements.send')}
                </button>
                <Link to="/admin/announcements">{t('common.cancel')}</Link>
              </div>
              <p className="muted small">{t('admin_announcements.send_warning')}</p>
            </>
          )}
        </section>
      </form>
    </main>
  )
}
